import { useState, useEffect, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import { BarChart3, GitCompare, Lock } from 'lucide-react'
import { YearRangeFilter } from './shared'
import {
  KPICardsSection,
  HighYieldUnitsSection,
  SubjectAnalysisSection,
  PredictionsSection,
  ExamHistorySection,
} from './sections'
import { useActivationAccess, UnlockDialog } from '@/features/payments'
import { trackUpgradeCtaClicked, trackAnalyticsViewed } from '@/shared/utils/analytics'
import { fbTrackViewContent } from '@/shared/utils/facebookPixel'

export default function PastExamsDashboard() {
  const navigate = useNavigate()
  const { isPaidUser } = useActivationAccess()
  // Locked until the user is known to hold a redeemed activation key
  const isLocked = !isPaidUser
  const [showUnlock, setShowUnlock] = useState(false)
  const analyticsTrackedRef = useRef(false)

  // Track ViewContent for FB Pixel
  useEffect(() => {
    fbTrackViewContent('analytics', 'past_exams', 'Past Exams Analytics')
  }, [])

  // Track analytics_viewed for GA4
  useEffect(() => {
    if (!analyticsTrackedRef.current) {
      analyticsTrackedRef.current = true
      trackAnalyticsViewed('past_exam')
    }
  }, [])

  const handleCompareClick = () => {
    if (isLocked) {
      trackUpgradeCtaClicked('analytics_compare_topics')
      setShowUnlock(true)
    } else {
      navigate('/analytics/past-exams/compare')
    }
  }

  return (
    <div className="space-y-4 sm:space-y-6">
      {/* Header */}
      <div className="bg-white rounded-lg shadow-sm p-4 sm:p-6">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 flex items-center gap-3">
              <BarChart3 className="w-8 h-8 text-blue-600" />
              Past Exams Analytics
            </h1>
            <p className="text-sm sm:text-base text-gray-600 mt-1">
              Analyze PYQ patterns, trends, and high-yield topics to optimize your preparation
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-3">
            <YearRangeFilter />
            <button
              onClick={handleCompareClick}
              className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              {isLocked ? <Lock className="w-4 h-4" /> : <GitCompare className="w-4 h-4" />}
              Compare Topics
            </button>
          </div>
        </div>
      </div>

      {/* Section 1: KPI Cards */}
      <KPICardsSection />

      {/* Section 2: High-Yield Units - Locked for free users (tabs remain accessible) */}
      <HighYieldUnitsSection isLocked={isLocked} />

      {/* Section 4: Subject Analysis - Locked for free users (tabs remain accessible) */}
      <SubjectAnalysisSection isLocked={isLocked} />

      {/* Section 5: Must-Study Predictions - Free for everyone */}
      <PredictionsSection />

      {/* Section 7: Exam History Browser */}
      <ExamHistorySection />

      <UnlockDialog
        isOpen={showUnlock}
        onClose={() => setShowUnlock(false)}
        title="Unlock topic comparison"
        description="Compare how topics have been asked across NEET papers."
        source="analytics_compare_topics"
      />
    </div>
  )
}
